import React from "react";

// COACHING PERSONALITY — one axis drawn as a spectrum with a marker.
//
// Nothing in the repo does this (the closest neighbours, CircularProgress and
// the ListingComposer stepper, draw progress, which is a different thing: a
// spectrum has no "more" end). So it is built here, once, and every surface
// that shows an axis uses it: the inner evaluation, the club side card, the
// card's detail view and the owner's private development comparison.
//
// THE POSITION IS NEVER COMPUTED HERE. `marker_position` arrives from the
// server (coaching_personality.py:465, proportional to the axis sum, rounded to
// four places, 0.0 at the left pole and 1.0 at the right). This component turns
// it into a percentage and nothing else. There is no sum in the club side
// payload at all, so a client that did the arithmetic could not draw the card.
//
// Three sizes, one geometry, taken from the accepted prototype:
//   inner  6px track, 12px marker with a 3px white ring   (evaluation)
//   zoom   5px track, 12px marker with a 3px white ring   (card detail view)
//   card   4px track,  7px marker with a 2px white ring   (profile card)
//
// RTL: `left` is a physical offset and does not flip with `dir`, while the pole
// labels above the track DO flip (they sit in a flex row). Mirroring the offset
// keeps the marker on the pole it belongs to. See locale.js.

const SIZES = {
  inner: { track: "h-[6px] rounded-[3px]", marker: 12, ring: 3 },
  zoom: { track: "h-[5px] rounded-[3px]", marker: 12, ring: 3 },
  card: { track: "h-[4px] rounded-[2px]", marker: 7, ring: 2 },
};

const clamp01 = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  if (number < 0) return 0;
  if (number > 1) return 1;
  return number;
};

const Marker = ({ position, size, ring, rtl, hollow, title }) => {
  const fraction = clamp01(position);
  const offset = rtl ? 1 - fraction : fraction;
  return (
    <span
      title={title}
      style={{
        position: "absolute",
        top: "50%",
        left: `${offset * 100}%`,
        transform: "translate(-50%, -50%)",
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        background: hollow ? "#FFFFFF" : "#2183FE",
        border: hollow
          ? `${ring}px solid #2183FE`
          : `${ring}px solid #FFFFFF`,
        boxShadow: "0 1px 4px rgba(17,24,39,0.3)",
        boxSizing: "content-box",
        display: "block",
      }}
    />
  );
};

/**
 * @param {number}  position          0..1, straight from the server
 * @param {number}  previousPosition  0..1, the earlier result (owner only)
 * @param {string}  size              inner | zoom | card
 * @param {boolean} rtl               mirror the physical offset
 */
const Spectrum = ({ position, previousPosition = null, size = "inner", rtl = false }) => {
  const spec = SIZES[size] || SIZES.inner;
  return (
    <div
      className={`relative bg-[#E5E7EB] ${spec.track} ${
        size === "card" ? "mt-[7px]" : "mt-[8px] mx-[2px]"
      }`}
    >
      {previousPosition !== null && previousPosition !== undefined && (
        <Marker
          position={previousPosition}
          size={spec.marker}
          ring={spec.ring}
          rtl={rtl}
          hollow
        />
      )}
      <Marker position={position} size={spec.marker} ring={spec.ring} rtl={rtl} />
    </div>
  );
};

export default Spectrum;
